import { getSourceDimensions } from '@onefootprint/vision';
import type { MutableRefObject } from 'react';
import { getLogger, Logger } from '@/idv2/utils/logger';
import type { AutoCaptureKind, Resolution, VideoRef } from '../types';

type GetImageStringProps = {
  context: CanvasRenderingContext2D;
  videoRef: VideoRef;
  canvasRef: MutableRefObject<HTMLCanvasElement | undefined>;
  videoResolution?: Resolution;
  desiredImageWidth: number;
  desiredImageHeight: number;
  autoCaptureKind: AutoCaptureKind;
  centerOffsetX?: number;
  centerOffsetY?: number;
};

const { logWarn } = getLogger({ location: 'camera' });

const useGetImageString = () => {
  const getImageStringFromVideo = async ({
    context,
    videoRef,
    canvasRef,
    videoResolution,
    desiredImageWidth,
    desiredImageHeight,
    autoCaptureKind: _autoCaptureKind,
    centerOffsetX = 0,
    centerOffsetY = 0,
  }: GetImageStringProps) => {
    if (!videoRef.current || !canvasRef.current) {
      return null;
    }

    const sourceDimensions = getSourceDimensions({
      videoRef,
      videoResolution,
      desiredImageWidth,
      desiredImageHeight,
      centerOffsetX,
      centerOffsetY,
    });

    const { sx, sy, sWidth, sHeight } = sourceDimensions;

    if (sWidth <= 0 || sHeight <= 0) {
      Logger.error(
        `Computed desired image dimensions is 0 or negative number - sx: ${sx}, sy: ${sy}, width: ${sWidth}, height: ${sHeight}`,
      );
      return null;
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';

    canvasRef.current.setAttribute('width', `${sWidth}`);
    canvasRef.current.setAttribute('height', `${sHeight}`);

    try {
      context.drawImage(
        videoRef.current,
        sx,
        sy,
        sWidth,
        sHeight,
        0,
        0,
        canvasRef.current?.clientWidth,
        canvasRef.current?.clientHeight,
      );
    } catch (error) {
      logWarn('Error drawing image from video', error);
      return null;
    }

    const imageString = canvasRef.current.toDataURL();

    return imageString;
  };

  return getImageStringFromVideo;
};

export default useGetImageString;
